// components/industry-templates/TravelEventTemplate.tsx
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Plane, MapPin, Camera, Palmtree } from 'lucide-react';

export const TravelEventTemplate = ({ structure, theme }: any) => {
  const industryData = structure.industry_data;

  return (
    <div className="space-y-8">
      {/* Destinations Showcase */}
      {industryData?.destination_showcase && (
        <Card className="border-0 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2 text-2xl">
              <Palmtree className="w-8 h-8" style={{ color: theme.primary }} />
              Featured Destinations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {industryData.destination_showcase.map((destination: any, idx: number) => (
                <div key={idx} className="p-5 bg-white/10 rounded-xl border border-white/20 hover:scale-105 transition-transform">
                  <div className="text-4xl mb-3">{destination.emoji || '🌍'}</div>
                  <h3 className="text-xl font-bold text-white mb-2">{destination.name}</h3>
                  <p className="text-slate-300 text-sm mb-3">{destination.description}</p>
                  <div className="flex gap-2 flex-wrap">
                    <Badge style={{ backgroundColor: theme.primary }}>
                      {destination.package_price}
                    </Badge>
                    <Badge variant="outline" className="border-white/20 text-white">
                      {destination.duration}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Travel Deals */}
      {industryData?.special_deals && (
        <Card className="border-2" style={{ borderColor: theme.primary, backgroundColor: 'rgba(6, 182, 212, 0.1)' }}>
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Plane className="w-6 h-6" style={{ color: theme.primary }} />
              Exclusive Travel Deals
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {industryData.special_deals.map((deal: any, idx: number) => (
                <div key={idx} className="p-5 bg-white/10 rounded-xl">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="text-xl font-bold text-white mb-1">{deal.destination}</h3>
                      <p className="text-slate-300">{deal.description}</p>
                    </div>
                    <Badge className="bg-red-500 text-white text-lg py-2 px-4">
                      {deal.discount} OFF
                    </Badge>
                  </div>
                  <div className="flex gap-3 mt-3">
                    <Badge style={{ backgroundColor: theme.primary }}>
                      {deal.validity}
                    </Badge>
                    {deal.limited_slots && (
                      <Badge className="bg-yellow-500 text-black">
                        Limited Slots
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tour Operators */}
      {industryData?.tour_operators && (
        <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <MapPin className="w-6 h-6" style={{ color: theme.primary }} />
              Participating Tour Operators
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {industryData.tour_operators.map((operator: string, idx: number) => (
                <div key={idx} className="p-4 bg-white/5 rounded-lg text-center border border-white/10 hover:bg-white/10 transition-colors">
                  <div className="text-2xl mb-2">✈️</div>
                  <p className="text-white font-medium text-sm">{operator}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Travel Workshops */}
      {industryData?.travel_workshops && (
        <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Camera className="w-6 h-6" style={{ color: theme.primary }} />
              Travel Photography & Tips
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {industryData.travel_workshops.map((workshop: any, idx: number) => (
                <div key={idx} className="p-4 bg-white/5 rounded-lg border-l-4" style={{ borderColor: theme.primary }}>
                  <h4 className="font-bold text-white mb-1">{workshop.topic}</h4>
                  <p className="text-sm text-slate-400 mb-2">{workshop.expert}</p>
                  <div className="flex gap-2">
                    <Badge style={{ backgroundColor: theme.primary }}>{workshop.duration}</Badge>
                    <Badge variant="outline" className="border-white/20 text-white">{workshop.time}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Visa Assistance */}
      {industryData?.visa_assistance && (
        <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white">Visa Assistance Desk</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-white/5 rounded-lg p-5">
              <p className="text-white mb-3">Get expert guidance on visa processing for:</p>
              <div className="flex flex-wrap gap-2">
                {industryData.visa_assistance.countries?.map((country: string, idx: number) => (
                  <Badge key={idx} variant="outline" className="border-white/20 text-white">
                    {country}
                  </Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
